import type { CategoryBrpEntry } from '@gumtree/shared/src/util/category-brp-entry-cookie';

import allCategoriesIcon from '../images/assets/all-categories.png';
import appliancesIcon from '../images/assets/appliances.png';
import babyKidsIcon from '../images/assets/baby-kids.png';
import carsIcon from '../images/assets/cars.png';
import diyIcon from '../images/assets/diy.png';
import freebiesIcon from '../images/assets/freebies.png';
import homeGardenIcon from '../images/assets/home-garden.png';
import petsIcon from '../images/assets/pets.png';
import phonesIcon from '../images/assets/phones.png';
import propertyIcon from '../images/assets/property.png';
import servicesIcon from '../images/assets/services.png';
import sportsIcon from '../images/assets/sports.png';
import vansIcon from '../images/assets/vans.png';

import {
    isCategoryBrpEntryPath,
    redirectCategoryLandingPageEntriesToBrp,
    type CategoryBrpEntryExperiments,
    type CategoryDataItem,
    type CategoryDataItemSubItem,
} from '../reducer';

export type MhcCategoryLevel = 'L1' | 'L2' | 'ACTION';

export interface MhcCategoryItem {
    label: string;
    path: string | null;
    seoName: string;
    level: MhcCategoryLevel;
    iconSrc?: string;
    brpEntry?: CategoryBrpEntry;
}

export interface MhcTreeItem extends MhcCategoryItem {
    children: MhcCategoryItem[];
}

const assetSrc = (asset: string | { default?: string }) =>
    typeof asset === 'string' ? asset : asset.default;

const TREE_ICON_BY_SEO_NAME = {
    'cars-vans-motorbikes': assetSrc(require('../images/assets/icon-car.svg')),
    'for-sale': assetSrc(require('../images/assets/icon-sale.svg')),
    'business-services': assetSrc(require('../images/assets/icon-services.svg')),
    'flats-houses': assetSrc(require('../images/assets/icon-properties.svg')),
    pets: assetSrc(require('../images/assets/icon-pets.svg')),
    jobs: assetSrc(require('../images/assets/icon-jobs.svg')),
    community: assetSrc(require('../images/assets/icon-community.svg')),
};

const C_ICON_CONFIG = [
    { label: 'Cars', seoName: 'cars-vans-motorbikes', iconSrc: assetSrc(carsIcon), level: 'L1' },
    {
        label: 'Home & Garden',
        seoName: 'home-garden',
        iconSrc: assetSrc(homeGardenIcon),
        level: 'L2',
    },
    {
        label: 'Services',
        seoName: 'business-services',
        iconSrc: assetSrc(servicesIcon),
        level: 'L1',
    },
    { label: 'Properties', seoName: 'flats-houses', iconSrc: assetSrc(propertyIcon), level: 'L1' },
    { label: 'Pets', seoName: 'pets', iconSrc: assetSrc(petsIcon), level: 'L1' },
    {
        label: 'Sports & Leisure',
        seoName: 'sports-leisure-travel',
        iconSrc: assetSrc(sportsIcon),
        level: 'L2',
    },
    {
        label: 'Appliances',
        seoName: 'kitchen-appliances',
        iconSrc: assetSrc(appliancesIcon),
        level: 'L2',
    },
    { label: 'DIY', seoName: 'diy-tools-materials', iconSrc: assetSrc(diyIcon), level: 'L2' },
    { label: 'Mobile Phones', seoName: 'phones', iconSrc: assetSrc(phonesIcon), level: 'L2' },
    {
        label: 'Baby & Kids',
        seoName: 'baby-kids-stuff',
        iconSrc: assetSrc(babyKidsIcon),
        level: 'L2',
    },
    { label: 'Vans', seoName: 'vans', iconSrc: assetSrc(vansIcon), level: 'L2' },
    { label: 'Freebies', seoName: 'freebies', iconSrc: assetSrc(freebiesIcon), level: 'L2' },
] as const;

const toL2Item = (
    { name, path, seoName }: CategoryDataItemSubItem,
    brpEntry?: CategoryBrpEntry
): MhcCategoryItem => ({
    label: name,
    path,
    seoName,
    level: 'L2',
    brpEntry,
});

const getMobileBrpEntry = (
    seoName: string,
    path: string | null,
    { cars = false, services = false }: CategoryBrpEntryExperiments = {}
): CategoryBrpEntry | undefined => {
    if (!path) return undefined;

    if (seoName === 'cars-vans-motorbikes' && cars && isCategoryBrpEntryPath(path)) {
        return 'cars:mobile';
    }

    // Services B root entry writes the cookie used to establish the hotspot history state.
    if (
        seoName === 'business-services' &&
        services &&
        (isCategoryBrpEntryPath(path) || path === '/search' || path.startsWith('/search?'))
    ) {
        return 'services:mobile';
    }

    return undefined;
};

const findCategoryBySeoName = (items: CategoryDataItem[], seoName: string) =>
    items.find((item) => item.seoName === seoName);

const findSubcategoryBySeoName = (items: CategoryDataItem[], seoName: string) =>
    items.flatMap((item) => item.subitems || []).find((item) => item.seoName === seoName);

export const buildMhcTagItems = (
    items: CategoryDataItem[],
    brpEntryExperiments?: CategoryBrpEntryExperiments
): MhcCategoryItem[] =>
    redirectCategoryLandingPageEntriesToBrp(items, brpEntryExperiments).map(
        ({ name, path, seoName }) => ({
            label: name,
            path,
            seoName,
            level: 'L1',
            brpEntry: getMobileBrpEntry(seoName, path, brpEntryExperiments),
        })
    );

export const buildMhcIconItems = (
    items: CategoryDataItem[],
    brpEntryExperiments?: CategoryBrpEntryExperiments
): MhcCategoryItem[] => {
    const redirectedItems = redirectCategoryLandingPageEntriesToBrp(items, brpEntryExperiments);
    const iconItems = C_ICON_CONFIG.map(({ label, seoName, iconSrc, level }) => {
        const category =
            level === 'L1'
                ? findCategoryBySeoName(redirectedItems, seoName)
                : findSubcategoryBySeoName(redirectedItems, seoName);

        return {
            label,
            path: category?.path || null,
            seoName,
            level,
            iconSrc,
            brpEntry: getMobileBrpEntry(seoName, category?.path || null, brpEntryExperiments),
        };
    });

    return [
        ...iconItems,
        {
            label: 'All Categories',
            path: null,
            seoName: 'all-categories',
            level: 'ACTION',
            iconSrc: assetSrc(allCategoriesIcon),
        },
    ];
};

export const buildMhcTreeItems = (
    items: CategoryDataItem[],
    brpEntryExperiments?: CategoryBrpEntryExperiments
): MhcTreeItem[] =>
    redirectCategoryLandingPageEntriesToBrp(items, brpEntryExperiments).map(
        ({ name, path, seoName, subitems = [] }) => ({
            label: name,
            path,
            seoName,
            level: 'L1',
            iconSrc: TREE_ICON_BY_SEO_NAME[seoName],
            brpEntry: getMobileBrpEntry(seoName, path, brpEntryExperiments),
            children: subitems.map((subitem) =>
                toL2Item(
                    subitem,
                    seoName === 'cars-vans-motorbikes' &&
                        subitem.seoName === 'cars' &&
                        brpEntryExperiments?.cars &&
                        isCategoryBrpEntryPath(subitem.path)
                        ? 'cars:mobile'
                        : undefined
                )
            ),
        })
    );
